<template>
  <div v-if="school !== null" class="ui grid" style="width: 100%">
    <div class="row">
      <h3 class="ui huge header">Manage licenses</h3>
    </div>
    <div class="ui divider"></div>
    <div class="row">
      <sui-button content="Add license" positive icon="plus" v-on:click="showAddLicenseModal()">
      </sui-button>
    </div>
    <div v-if="removeLicenseError" class="ui negative message">
      <p>{{ removeLicenseError }}</p>
    </div>
    <div class="row">
      <licenses-table v-bind:licenses="licenses" v-bind:actions="licensesActions" v-on:rowAction="rowAction" />
    </div>
    <portal to="modal-destination" :order="1">
      <sui-modal v-model="licenseModalOpen">
        <sui-modal-header>License</sui-modal-header>
        <sui-modal-content class="scrolling">
          <license-form v-bind:school="school" v-bind:license="license" v-bind:error="licenseError"
            v-on:saved="licenseSaved" create="true" ref="licenseForm" />
        </sui-modal-content>
        <sui-modal-actions>
          <sui-button negative v-on:click="hideLicenseModal">
            Cancel
          </sui-button>
        </sui-modal-actions>
      </sui-modal>
      <sui-modal v-model="payLicenseModalOpen">
        <sui-modal-header>Pay license</sui-modal-header>
        <sui-modal-content class="scrolling">
          <pay-license-form v-bind:amount="amount" v-bind:error="payLicenseError" v-on:saved="payLicenseSaved"
            create="true" ref="payLicenseForm" />
        </sui-modal-content>
        <sui-modal-actions>
          <sui-button negative v-on:click="hidePayLicenseModal">
            Cancel
          </sui-button>
        </sui-modal-actions>
      </sui-modal>
    </portal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Moment from 'moment';
import LicensesTable from '@/components/licenses/LicensesTable';
import LicenseForm from '@/components/licenses/LicenseForm';
import PayLicenseForm from '@/components/licenses/PayLicenseForm';

export default {
  name: 'ManageLicenses',
  components: { LicensesTable, LicenseForm, PayLicenseForm },
  props: ['school'],
  data() {
    return {
      licenses: [],
      licensesActions: ['Update', 'Pay', 'Remove'],
      licenseModalOpen: false,
      licenseError: null,
      payLicenseModalOpen: false,
      payLicenseError: null,
      removeLicenseError: null,
      newLicense: false,
      amount: {
        licenseId: null,
        schoolId: this.school.id,
        date: Moment().format('YYYY-MM-DD'),
        amount: 0.0,
        balance: 0.0,
      },
      license: {
        schoolId: this.school.id,
        number: Moment().format('YYYYMMDDHHmmss'),
        studentCount: this.school.totalStudentCount,
        durationMonths: 12,
        fixedPrice: 0.0,
        startDate: Moment().format('YYYY-MM-DD'),
        vatRate: 23,
        vatChargedOnPercentage: 20,
        discount: 0.0,
        pricePerStudent: 5.0,
        licenseModel: 'perstudent',
        status: 'final',
        dueInDays: 30,
        description: '',
        lineItems: [],
      },
    };
  },
  methods: {
    rowAction(action) {
      if (action.action === 'Remove') {
        this.$dialog
          .confirm('Please confirm to remove this license')
          .then(() => {
            this.$store
              .dispatch('removeLicense', {
                schoolId: this.school.id,
                licenseId: action.id,
              })
              .then(
                () => {
                  this.removeLicenseError = null;
                  this.$store.dispatch('getSchoolLicenses', { id: this.school.id, licenseModels: ['perstudent', 'fixedprice'] }).then((response) => {
                    this.licenses = response.data;
                  });
                },
                (error) => {
                  this.removeLicenseError = error.response.data;
                },
              );
          })
          .catch(() => { });
      }
      if (action.action === 'Update') {
        // eslint-disable-next-line arrow-parens
        this.license = this.licenses.find((l) => l.id === action.id);
        this.license.startDate = Moment(this.license.startDate).format(
          'YYYY-MM-DD',
        );
        this.newLicense = false;
        this.licenseError = null;
        this.$refs.licenseForm.reset();
        this.licenseModalOpen = true;
      }
      if (action.action === 'Pay') {
        // eslint-disable-next-line arrow-parens
        this.license = this.licenses.find((l) => l.id === action.id);
        this.showPayLicenseModal(action.id, this.license.balance);
      }
    },
    showAddLicenseModal() {
      this.newLicense = true;
      this.license = {
        schoolId: this.school.id,
        number: Moment().format('YYYYMMDDHHmmss'),
        studentCount: this.school.totalStudentCount,
        durationMonths: 12,
        startDate: Moment().format('YYYY-MM-DD'),
        fixedPrice: 0.0,
        vatRate: 23,
        vatChargedOnPercentage: 20,
        discount: 0.0,
        pricePerStudent: 3.0,
        licenseModel: 'perstudent',
        status: 'final',
        lineItems: [],
        dueInDays: 30,
        description: '',
      };
      this.licenseError = null;
      this.$refs.licenseForm.reset();
      this.licenseModalOpen = true;
    },
    showPayLicenseModal(licenseId, balance) {
      this.amount = {
        licenseId,
        schoolId: this.school.id,
        amount: balance,
        date: Moment().format('YYYY-MM-DD'),
        balance,
      };
      this.payLicenseError = null;
      this.$refs.payLicenseForm.reset();
      this.payLicenseModalOpen = true;
    },
    hideLicenseModal() {
      this.licenseModalOpen = false;
    },
    hidePayLicenseModal() {
      this.payLicenseModalOpen = false;
    },
    payLicenseSaved() {
      this.$store.dispatch('payLicense', this.amount).then(
        () => {
          this.payLicenseError = null;
          this.$store.dispatch('getSchoolLicenses', { id: this.school.id, licenseModels: ['perstudent', 'fixedprice'] }).then((response) => {
            this.licenses = response.data;
          });
          this.hidePayLicenseModal();
        },
        (error) => {
          this.payLicenseError = error.response.data;
        },
      );
    },
    licenseSaved() {
      this.license.durationMonths = parseInt(this.license.durationMonths, 10);
      this.license.dueInDays = parseInt(this.license.dueInDays, 10);
      if (this.newLicense) {
        this.$store.dispatch('addLicense', this.license).then(
          () => {
            this.licenseError = null;
            this.$store.dispatch('getSchoolLicenses', { id: this.school.id, licenseModels: ['perstudent', 'fixedprice'] }).then((response) => {
              this.licenses = response.data;
            });
            this.hideLicenseModal();
          },
          (error) => {
            this.licenseError = error.response.data;
          },
        );
      } else {
        this.$store.dispatch('updateLicense', this.license).then(
          () => {
            this.licenseError = null;
            this.$store.dispatch('getSchoolLicenses', { id: this.school.id, licenseModels: ['perstudent', 'fixedprice'] }).then((response) => {
              this.licenses = response.data;
            });
            this.hideLicenseModal();
          },
          (error) => {
            this.licenseError = error.response.data;
          },
        );
      }
    },
  },
  created() {
    this.$store.dispatch('getSchoolLicenses', { id: this.school.id, licenseModels: ['perstudent', 'fixedprice'] }).then((response) => {
      this.licenses = response.data;
    });
  },
};
</script>



// WEBPACK FOOTER //
// src/components/licenses/ManageLicenses.vue